import { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { getInitials, getAvatarColor } from '@/lib/avatar';
import { useDeleteUser } from '@/hooks/useUsers';
import { useToast } from '@/components/ui/use-toast';
import type { UserListItem } from '@/lib/api';

interface DeleteUserDialogProps {
  open: boolean;
  onClose: () => void;
  user: UserListItem | null;
}

/**
 * DeleteUserDialog Component
 *
 * Confirmation dialog for deleting (deactivating) a user.
 * Matches the approved UI design in docs/views/settings.html lines 1155-1186.
 *
 * Tasks: M4.5.5.1, M4.5.5.4
 */
export function DeleteUserDialog({ open, onClose, user }: DeleteUserDialogProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const deleteUserMutation = useDeleteUser();
  const { addToast } = useToast();

  const handleDelete = async () => {
    if (!user) return;

    setIsDeleting(true);
    try {
      await deleteUserMutation.mutateAsync(user.id);
      addToast(`${user.name} has been deactivated successfully`, 'success');
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete user';
      addToast(message, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (!isDeleting) {
      onClose();
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogContent className="w-full max-w-md p-6">
        {/* Header with warning icon */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-destructive" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Delete User?</h3>
            <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
          </div>
        </div>

        {/* Confirmation text */}
        <p className="text-sm text-muted-foreground mb-2">
          Are you sure you want to delete this user?
        </p>

        {/* User info card */}
        <div className="p-3 rounded-lg bg-muted/50 mb-4">
          <div className="flex items-center gap-3">
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center ${getAvatarColor(user.name)}`}
            >
              <span className="text-xs font-medium">{getInitials(user.name)}</span>
            </div>
            <div>
              <p className="font-medium text-sm text-foreground">{user.name}</p>
              <p className="text-xs text-muted-foreground">{user.email}</p>
            </div>
          </div>
        </div>

        {/* Warning message */}
        <p className="text-sm text-muted-foreground mb-6">
          The user will lose access to the system immediately. Any records owned by this user will
          remain but can be reassigned.
        </p>

        {/* Action buttons */}
        <div className="flex justify-end gap-3">
          <button
            onClick={handleClose}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-destructive-foreground bg-destructive rounded-lg hover:bg-destructive/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
            {isDeleting ? 'Deleting...' : 'Delete User'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
